"use client"

import { useState } from "react"
import { Download, Loader2, CheckCircle2, User, Building2, Mail } from "lucide-react"
import type { Idioma } from "@/lib/landing/idioma"

/**
 * El formulario de descarga de las plantillas, en los dos idiomas. Es el de
 * components/public/template-download-form.tsx con un diccionario: el
 * castellano dice lo mismo que aquel.
 */
const T = {
  es: {
    boton: "Descargar gratis",
    faltan: "Nombre, apellidos y email son obligatorios",
    emailMal: "Introduce un email válido",
    privacidadFalta: "Debes aceptar la política de privacidad",
    titulo: "Descarga tu plantilla",
    subtitulo: "Rellena tus datos para descargar",
    nombre: "Nombre *",
    apellidos: "Apellidos *",
    empresa: "Nombre de empresa",
    email: "Email profesional *",
    aceptoAntes: "He leído y acepto la",
    privacidad: "política de privacidad",
    novedades: "Acepto recibir novedades y consejos para mi restaurante por email.",
    procesando: "Procesando...",
    iniciada: "¡Descarga iniciada!",
    ahora: "Descargar ahora",
    sinSpam: "Sin spam. Tus datos están seguros.",
  },
  en: {
    boton: "Download free",
    faltan: "First name, surname and email are required",
    emailMal: "Please enter a valid email address",
    privacidadFalta: "You need to accept the privacy policy",
    titulo: "Download your template",
    subtitulo: "Fill in your details to download",
    nombre: "First name *",
    apellidos: "Surname *",
    empresa: "Company name",
    email: "Work email *",
    aceptoAntes: "I have read and accept the",
    privacidad: "privacy policy",
    novedades: "I would like to receive news and tips for my business by email.",
    procesando: "Processing...",
    iniciada: "Download started!",
    ahora: "Download now",
    sinSpam: "No spam. Your details are safe.",
  },
} as const

interface FormularioDescargaProps {
  templateSlug: string
  downloadFile: string
  colorGradient: string
  label?: string
  idioma?: Idioma
}

export default function FormularioDescarga({
  templateSlug,
  downloadFile,
  colorGradient,
  label,
  idioma = "es",
}: FormularioDescargaProps) {
  const t = T[idioma]
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [done, setDone] = useState(false)
  const [form, setForm] = useState({
    nombre: "",
    apellidos: "",
    empresa: "",
    email: "",
    acepta_privacidad: false,
    acepta_novedades: false,
  })
  const [error, setError] = useState("")

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")

    if (!form.nombre.trim() || !form.apellidos.trim() || !form.email.trim()) {
      setError(t.faltan)
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(form.email)) {
      setError(t.emailMal)
      return
    }

    if (!form.acepta_privacidad) {
      setError(t.privacidadFalta)
      return
    }

    setLoading(true)
    try {
      await fetch("/api/plantillas/download", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          plantilla: templateSlug,
        }),
      })

      // Trigger download regardless of API result
      setDone(true)
      const link = document.createElement("a")
      link.href = downloadFile
      link.download = ""
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      setTimeout(() => {
        setDone(false)
        setIsOpen(false)
      }, 3000)
    } catch {
      // Still allow download even if API fails
      const link = document.createElement("a")
      link.href = downloadFile
      link.download = ""
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      setIsOpen(false)
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className={`inline-flex items-center justify-center gap-2 bg-gradient-to-r ${colorGradient} text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all hover:opacity-90 group`}
      >
        <Download className="w-5 h-5" />
        {label ?? t.boton}
      </button>
    )
  }

  return (
    <div className="w-full max-w-md">
      <div className="bg-white rounded-2xl border border-zinc-200 shadow-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-10 h-10 rounded-lg bg-gradient-to-r ${colorGradient} flex items-center justify-center`}>
            <Download className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-zinc-900 text-sm">{t.titulo}</h3>
            <p className="text-xs text-zinc-500">{t.subtitulo}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <input
                type="text"
                placeholder={t.nombre}
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                className="w-full pl-9 pr-3 py-2.5 border border-zinc-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent"
              />
            </div>
            <div>
              <input
                type="text"
                placeholder={t.apellidos}
                value={form.apellidos}
                onChange={(e) => setForm({ ...form, apellidos: e.target.value })}
                className="w-full px-3 py-2.5 border border-zinc-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="relative">
            <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input
              type="text"
              placeholder={t.empresa}
              value={form.empresa}
              onChange={(e) => setForm({ ...form, empresa: e.target.value })}
              className="w-full pl-9 pr-3 py-2.5 border border-zinc-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent"
            />
          </div>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input
              type="email"
              placeholder={t.email}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full pl-9 pr-3 py-2.5 border border-zinc-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent"
            />
          </div>

          <label className="flex items-start gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={form.acepta_privacidad}
              onChange={(e) => setForm({ ...form, acepta_privacidad: e.target.checked })}
              className="mt-0.5 h-4 w-4 rounded border-zinc-300 text-lime-600 focus:ring-lime-500 accent-lime-600"
            />
            <span className="text-xs text-zinc-500 leading-relaxed">
              {t.aceptoAntes}{" "}
              <a href={idioma === "en" ? "/en/legal/privacy" : "/legal/privacidad"} target="_blank" className="text-lime-600 underline hover:text-lime-700">
                {t.privacidad}
              </a>
              . *
            </span>
          </label>

          <label className="flex items-start gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={form.acepta_novedades}
              onChange={(e) => setForm({ ...form, acepta_novedades: e.target.checked })}
              className="mt-0.5 h-4 w-4 rounded border-zinc-300 text-lime-600 focus:ring-lime-500 accent-lime-600"
            />
            <span className="text-xs text-zinc-500 leading-relaxed">
              {t.novedades}
            </span>
          </label>

          {error && (
            <p className="text-sm text-red-500">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading || done}
            className={`w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r ${colorGradient} text-white px-6 py-3 rounded-xl font-semibold transition-all hover:opacity-90 disabled:opacity-70`}
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {t.procesando}
              </>
            ) : done ? (
              <>
                <CheckCircle2 className="w-5 h-5" />
                {t.iniciada}
              </>
            ) : (
              <>
                <Download className="w-5 h-5" />
                {t.ahora}
              </>
            )}
          </button>

          <p className="text-xs text-zinc-400 text-center">
            {t.sinSpam}
          </p>
        </form>
      </div>
    </div>
  )
}
